import getConfig from "next/config";

import mockUpPromotionDataResponse from "@/libs/client/mocks/promotion/mockPromotionResponseSuccess.json";
import { mergePromotionProductTypesIntoParams } from "@/utils/common/other/convertPromotionToArrayType";
import { apiRequest } from "@/utils/common/serviceApiHelper/serviceApiUtils";

import { getPromotionData } from "./getPromotionData";
import { ResponseGetCategory } from "./promotionServiceInterface/getCategoryPromotionInterface";
import type { RequestCouponListInterface } from "./promotionServiceInterface/getCouponListInterface";
import { ResponseCouponList } from "./promotionServiceInterface/getCouponListInterface";
import type { RequestPromotionDetailInterface } from "./promotionServiceInterface/getPromotionDetailInterface";
import { ResponsePromotionDetail } from "./promotionServiceInterface/getPromotionDetailInterface";
import type { RequestPromotionLandingInterface } from "./promotionServiceInterface/getPromotionLandingInterface";
import { ResponsePromotionLanding } from "./promotionServiceInterface/getPromotionLandingInterface";
import type {
  PromotionProductTypeEnum,
  RequestPromotionListInterface,
  RequestPromotionListV5Interface,
  RequestRoomPromotionInterface,
} from "./promotionServiceInterface/getPromotionListInterface";
import {
  ResponsePromotionList,
  ResponsePromotionListV5,
} from "./promotionServiceInterface/getPromotionListInterface";
import { ResponsePromotionStatic } from "./promotionServiceInterface/getPromotionStaticInterface";

const config =
  typeof window === "undefined"
    ? getConfig()?.serverRuntimeConfig
    : getConfig()?.publicRuntimeConfig;

const cachesPromotionTime =
  config?.publicRuntimeConfig?.cachesPromotionTime || 1800;

class PromotionServiceApi {
  private baseUrl: string;

  private basePath: string;

  private basePathV2: string;

  private basePathV5: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.basePath = "promotion";
    this.basePathV2 = "v2/promotion";
    this.basePathV5 = "v5/promotion";
  }

  async getPromotionLanding(
    params: RequestPromotionLandingInterface,
  ): Promise<
    | ResponsePromotionLanding
    | ResponsePromotionDetail
    | ResponsePromotionStatic
    | null
  > {
    const queryParams = new URLSearchParams(params as any);
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/Landing?${queryParams}`,
        "GET",
      )) as any;

      if (response.status) {
        switch (response.destination_type) {
          case 0:
            return new ResponsePromotionDetail(response);
          case 1:
            return new ResponsePromotionStatic(response);
          case 2:
            return new ResponsePromotionLanding(response, params.slug);
          default:
            return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async getPromotionListV2(
    params: RequestPromotionListInterface,
  ): Promise<ResponsePromotionList> {
    let promotionProductTypes: PromotionProductTypeEnum[];
    const { type, category } = params;
    const responseJson = await getPromotionData(
      Array.isArray(type) ? type[0]! : type,
      category ? Number(category[0]) : undefined,
    );

    if (responseJson && responseJson.total > 0) {
      return new ResponsePromotionList(responseJson);
    }

    if (Array.isArray(type)) {
      promotionProductTypes = type;
    } else {
      promotionProductTypes = [type];
    }

    let promotionProductCategory: string[];
    if (params.category) {
      if (Array.isArray(params.category)) {
        promotionProductCategory = params.category;
      } else {
        promotionProductCategory = [params.category];
      }
    } else {
      promotionProductCategory = [];
    }

    const newParams = mergePromotionProductTypesIntoParams(
      promotionProductTypes,
      params,
      promotionProductCategory,
    );

    const queryParams = new URLSearchParams(newParams as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/promotionLists?${queryParams}`,
      "GET",
    )) as any;
    return new ResponsePromotionList(response);
  }

  async getPromotionListV5(
    params: RequestPromotionListV5Interface,
  ): Promise<ResponsePromotionListV5> {
    const queryParams = new URLSearchParams(params as any);
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV5}/promotionList?${queryParams}`,
      "GET",
    )) as any;

    return new ResponsePromotionListV5(response.data ?? []);
  }

  async getCouponList(
    params: RequestCouponListInterface,
  ): Promise<ResponseCouponList | null> {
    try {
      const queryParams = new URLSearchParams(params as any);
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV5}/couponList?${queryParams}`,
        "GET",
      )) as any;

      return new ResponseCouponList(response);
    } catch (error) {
      return null;
    }
  }

  async getCategoryPromotion(): Promise<ResponseGetCategory> {
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/GetCategory`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    )) as any;
    return new ResponseGetCategory(response);
  }

  async getPromotionDetail(
    params: RequestPromotionDetailInterface,
  ): Promise<ResponsePromotionDetail> {
    const queryParams = new URLSearchParams(params as any);
    const revalidate = cachesPromotionTime;
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/Detail?${queryParams}`,
      "GET",
      {},
      false,
      undefined,
      false,
      revalidate,
    )) as any;
    return new ResponsePromotionDetail(response);
  }

  // ******************************************************
  // ****************** Method: POST **********************
  // ******************************************************
  async postRedeemPromotionCode(body: RequestRoomPromotionInterface) {
    if (process.env.NEXT_PUBLIC_NEXT_PUBLIC_MOCK_UP_API === "true") {
      return {
        mockUpPromotionDataResponse,
        status: 200,
      };
    }
    return apiRequest(
      this.baseUrl,
      `${this.basePath}/coupon/redeem`,
      "POST",
      body,
    );
  }
}

export default PromotionServiceApi;
